/**
 * Påmelding — the staff registration list for an event, its seat limit and waiting list, and check-in at the door.
 * MKK adoption Batch 2A (MKK_ADOPTION_BATCH_2.md §4, §11). Staff only; nothing here is public or in the app.
 *
 * Behind the `event_registration` capability (the menu item is absent without it, and every route answers 404). The
 * server says in every answer what this person may do (`can`), and decides again on each request:
 *   events.write                   the events and their COUNTS (the left list, the tiles) — never a name;
 *   events.attendees.read          also the names;
 *   events.checkin (+ capability)  «Sjekk inn» / «Angre» — one big button per person, idempotent on the server;
 *   events.registrations.manage    the list's settings, adding, editing, cancelling, moving up from the waiting list.
 *
 * One compact screen: a filter bar (upcoming/past, with or without a list, search), one row per event with its state and
 * counts, and the chosen event beside the list on a wide screen (its own view on a phone): tiles with the counts, the
 * settings, then the people — filtered by status and arrival, searched by name, fifty at a time. A check-in shows at once
 * and says «Lagrer …» until the server has it; a refusal puts it back with the reason. Every edit carries the version it
 * was based on; a conflict shows the newest and keeps what was typed. A second person with the same name is refused once
 * (409 duplicate) and added only on purpose («Legg til likevel»). Cancelling is two clicks. Escape closes the detail
 * when nothing is unsaved; focus returns to the row it came from.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { can, patch, post, send, errText, type Body } from '../lib/api';
import { useFlags, useLoad, useMsg } from '../lib/hooks';
import { useDirty, useNav } from '../lib/nav';
import { RamadanListsCard } from './RamadanLists';
import { wallClock } from '../lib/format';
import { Btn, Card, Field, Loading, LoadError, Msg, Tag, TwoStep, type TagTone } from '../components/ui';
import { FormDialog } from '../components/dialog';
import { BackButton, Conflict, MoreButton, hashQuery, useEscapeToClose, usePaged } from './work-ui';
import { STATUS_WORDS, withTones } from '../core';
import { TicketList } from '../components/EventPro';
import { AttendeePushButton } from '../components/AttendeePush';

// The words are core's (event-ops LIST_STATE_NO, STATUS_NO); the colour is the panel's.
const LIST_STATE: Record<string, [string, TagTone]> = withTones<TagTone>(STATUS_WORDS.listState, { open: 'green', closed: 'gold', cancelled: 'grey' }, 'grey');
const STATUS: Record<string, [string, TagTone]> = withTones<TagTone>(STATUS_WORDS.registration, { registered: 'green', waitlisted: 'gold', cancelled: 'grey' }, 'grey');
const WHEN: [string, string][] = [['upcoming', 'Kommende'], ['past', 'Tidligere'], ['all', 'Alle']];
const PEOPLE_VIEWS: [string, string][] = [['active', 'Påmeldt og venteliste'], ['registered', 'Påmeldt'], ['waitlisted', 'Venteliste'], ['cancelled', 'Avmeldt'], ['all', 'Alle']];
const ARRIVED: [string, string][] = [['any', 'Alle'], ['yes', 'Kommet'], ['no', 'Ikke kommet']];
const FIELD_LABELS = { displayName: 'Navn', partySize: 'Antall', note: 'Notat', capacity: 'Kapasitet', waitlist: 'Venteliste', state: 'Tilstand' };

const seats = (n: number) => `${n} ${n === 1 ? 'plass' : 'plasser'}`;
const people = (n: number) => `${n} ${n === 1 ? 'person' : 'personer'}`;

/** The app's answers («Kommer» / «Kommer ikke»), in words — never a name. Members who answer in the app are not on the list. */
function answerText(a: Body | null | undefined): string {
  if (!a || (!a.coming && !a.notComing)) return '';
  const parts = [];
  if (a.coming) parts.push(`${a.coming} kommer${a.comingPeople > a.coming ? ` (${people(a.comingPeople)})` : ''}`);
  if (a.notComing) parts.push(`${a.notComing} kommer ikke`);
  return 'I appen: ' + parts.join(', ');
}

/** The counts of one event's list, in words — never a name. */
function CountText({ s, a }: { s: Body | null; a?: Body | null }) {
  const app = answerText(a);
  if (!s) return <span className="reg-counts"><span>Ingen liste</span>{app && <span>· {app}</span>}{a && a.checkedInSeats > 0 && <span>· {a.checkedInSeats} kommet</span>}</span>;
  return (
    <span className="reg-counts">
      <span>{seats(s.registeredSeats)} påmeldt</span>
      {s.waitlistedEntries > 0 && <span>· {people(s.waitlistedEntries)} venter</span>}
      {(s.checkedInSeats + ((a && a.checkedInSeats) || 0)) > 0 && <span>· {s.checkedInSeats + ((a && a.checkedInSeats) || 0)} kommet</span>}
      {s.capacity !== null && <span>· {s.remaining} ledige av {s.capacity}</span>}
      {app && <span>· {app}</span>}
    </span>
  );
}

export function Pamelding() {
  const start = hashQuery();
  const w0 = start.get('when');
  const [when, setWhen] = useState(w0 === 'past' || w0 === 'all' ? w0 : 'upcoming');
  const [withList, setWithList] = useState(start.get('list') === 'yes' || start.get('list') === 'no' ? start.get('list')! : '');
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(start.get('id'));
  const lastRow = useRef<string | null>(null);
  useEffect(() => { const t = setTimeout(() => setQ(typed.trim()), 250); return () => clearTimeout(t); }, [typed]);
  const qs = new URLSearchParams({ when, ...(withList ? { list: withList } : {}), ...(q ? { q } : {}) });
  const list = usePaged('/event-ops/events?' + qs.toString());
  const close = useCallback(() => {
    setOpen(null);
    setTimeout(() => (document.getElementById(lastRow.current ? 'reg-row-' + lastRow.current : 'reg-head') as HTMLElement | null)?.focus(), 0);
  }, []);
  const items = list.items;
  const totals: Body | null = list.data ? list.data.totals : null;
  const canNames = Boolean(list.data && list.data.can && list.data.can.names);
  // The Ramadan day lists (2026-10-31): grouped by day under the events, only when the mosque has the Ramadan sign-up.
  const { caps } = useNav();
  const ramadanOn = Boolean(caps && caps.includes('ramadan_signup'));
  return (
    <>
    <div className={'work-split' + (open ? ' has-detail' : '')}>
      <Card className="work-list" title={<span id="reg-head" tabIndex={-1}>Påmelding</span>}
            hint={canNames ? 'Moskeens egne lister over hvem som kommer, med venteliste og innsjekking ved døren. Bare ansatte ser dem; ingenting sendes ut herfra.'
              : 'Antall påmeldte per arrangement. Navnene ser bare de som har fått tilgang til det.'}>
        <div className="work-bar">
          <div className="bnr-seg work-seg" role="group" aria-label="Når">
            {WHEN.map(([v, l]) => <button key={v} type="button" aria-pressed={when === v} onClick={() => setWhen(v)}>{l}</button>)}
          </div>
          <div className="bnr-seg work-seg" role="group" aria-label="Liste">
            <button type="button" aria-pressed={withList === ''} onClick={() => setWithList('')}>Alle</button>
            <button type="button" aria-pressed={withList === 'yes'} onClick={() => setWithList('yes')}>Med liste</button>
            <button type="button" aria-pressed={withList === 'no'} onClick={() => setWithList('no')}>Uten liste</button>
          </div>
        </div>
        <div className="work-bar">
          <Field label="Søk i arrangementer" className="field work-search">{(id) => (
            <input id={id} type="search" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Tittel" maxLength={120} />
          )}</Field>
        </div>
        {list.error ? <LoadError error={list.error} retry={list.reload} /> : !list.data ? <Loading /> : (
          <>
            <p className="vh" role="status">{list.total === 1 ? '1 arrangement' : `${list.total} arrangementer`}{items.length < list.total ? `, ${items.length} vist` : ''}</p>
            {totals && totals.lists > 0 && (
              <ul className="reg-tiles card-body" aria-label="Alle listene i utvalget" style={{ paddingTop: 0, paddingBottom: 0 }}>
                <li><b>{totals.registeredSeats}</b><span>plasser påmeldt · {totals.lists === 1 ? '1 liste' : `${totals.lists} lister`}</span></li>
                {totals.waitlistedEntries > 0 && <li><b>{totals.waitlistedEntries}</b><span>på venteliste</span></li>}
                {totals.checkedInSeats > 0 && <li><b>{totals.checkedInSeats}</b><span>kommet</span></li>}
                {totals.cancelledEntries > 0 && <li><b>{totals.cancelledEntries}</b><span>avmeldt</span></li>}
              </ul>
            )}
            {items.length === 0 && <p className="card-body work-empty">{q || withList ? 'Ingen arrangementer passer filteret.' : when === 'upcoming' ? 'Ingen kommende arrangementer.' : 'Ingen arrangementer.'}</p>}
            <ul className="work-rows">{items.map((x) => {
              const e: Body = x.event, s: Body | null = x.summary;
              const [sl, st] = s && s.listState ? LIST_STATE[s.listState] || [s.listState, 'grey'] : ['Ingen liste', 'grey'];
              return (
                <li key={e.id}>
                  <button type="button" id={'reg-row-' + e.id} className={'work-row' + (open === e.id ? ' sel' : '')} aria-current={open === e.id ? 'true' : undefined}
                          onClick={() => { lastRow.current = e.id; setOpen(e.id); }}>
                    <span className="work-row-title">{e.title}</span>
                    <span className="work-row-meta">
                      <time dateTime={e.startsAt}>{wallClock(e.startsAt)}</time>
                      {e.status === 'cancelled' && <Tag tone="red">Avlyst</Tag>}
                      <Tag tone={st as TagTone}>{sl}</Tag>
                      <CountText s={s} a={x.answers} />
                    </span>
                  </button>
                </li>
              );
            })}</ul>
            <MoreButton list={list} />
          </>
        )}
      </Card>
      {open && <EventDetail key={open} id={open} onClose={close} onChanged={() => void list.reload()} />}
    </div>
    {ramadanOn && <RamadanListsCard />}
    </>
  );
}

/* ---- one event: tiles, settings, people ---- */
function EventDetail({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const detail = useLoad<Body>('/event-ops/events/' + encodeURIComponent(id));
  const { caps } = useNav();
  // Who may see the buyers is the server's answer (403 → the list is simply not shown).
  const ticketsOn = Boolean(caps && caps.includes('event_tickets'));
  const [msg, say] = useMsg();
  const [busy, setBusy] = useState(false);
  // One flag per form (QA C, 2026-10-16): a clean settings form no longer clears a half-typed new entry's flag.
  const forms = useFlags();
  const unsaved = forms.any;
  const head = useRef<HTMLSpanElement>(null);
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (detail.data && !focused) { head.current?.focus(); setFocused(true); } }, [detail.data, focused]);
  useDirty('registration-' + id, unsaved);
  useEscapeToClose(head, onClose, unsaved || busy);
  if (detail.error) {
    // 404 here is also what a draft, a deleted event or a switched-off capability answer: said as "not available", not as an error of the person's.
    return <Card className="work-detail"><div className="card-body"><BackButton onClick={onClose} what="arrangementet" />
      <LoadError error={detail.error} retry={detail.reload} /></div></Card>;
  }
  if (!detail.data) return <Card className="work-detail"><Loading /></Card>;
  const d = detail.data;
  const ev: Body = d.event, l: Body | null = d.list, s: Body | null = d.summary, P: Body = d.can || {};
  const changed = (body: Body) => { detail.setData(body); onChanged(); };
  const createList = async () => {
    setBusy(true); say('Oppretter …');
    const r = await send(`/event-ops/events/${encodeURIComponent(id)}/list`, { method: 'PUT', body: JSON.stringify({}) });
    setBusy(false);
    if (!r.ok) return say(errText(r.body, FIELD_LABELS), 'err');
    changed(r.body); say('Påmeldingslisten er opprettet.', 'ok');
  };
  return (
    <Card className="work-detail" title={<span ref={head} tabIndex={-1}>{ev.title}</span>}>
      <div className="card-body">
        <BackButton onClick={onClose} what="arrangementet" />
        <p className="work-row-meta">
          <time dateTime={ev.startsAt}>{wallClock(ev.startsAt)}</time>
          {ev.status === 'cancelled' && <Tag tone="red">Arrangementet er avlyst</Tag>}
          {ev.status === 'archived' && <Tag tone="grey">Arkivert</Tag>}
          {l && <Tag tone={(LIST_STATE[l.state] || ['', 'grey'])[1]}>{(LIST_STATE[l.state] || [l.state])[0]}</Tag>}
        </p>
        <AppAnswers a={d.answers} />
        {/* «Svar i appen» by name, checked in at the door (084) — for those who may see the list's names. */}
        {P.names && d.answers && d.answers.coming > 0 && <AppAnswerList id={id} can={P} onChanged={changed} />}
        {/* «Send varsel til de påmeldte» (2026-11-01): only those signed up — counts in the dialog, never names. */}
        <AttendeePushButton event={ev} hasList={Boolean(l)} />
        {!l ? (
          <>
            <p className="work-empty">Ingen påmeldingsliste for dette arrangementet.</p>
            {P.manage && P.writable && <Btn disabled={busy} aria-busy={busy || undefined} onClick={createList}>Opprett påmeldingsliste</Btn>}
            {P.manage && !P.writable && <p className="note">Arrangementet er avlyst eller arkivert, så det kan ikke lages en liste.</p>}
          </>
        ) : (
          <>
            <Tiles s={s!} app={d.answers} />
            {s && s.storedLimitInactive && <p className="msg warn" role="note">Listen har en lagret kapasitet, men kapasitet og venteliste er ikke slått på for moskeen, så grensen gjelder ikke nå.</p>}
            {!P.writable && <p className="note">{ev.status === 'cancelled' || ev.status === 'archived' ? 'Arrangementet er avlyst eller arkivert: listen kan bare leses.' : 'Listen er avlyst og kan bare leses.'}</p>}
            {P.manage && P.writable && <ListSettings id={id} list={l} canCapacity={Boolean(P.capacity)} onChanged={changed} onUnsaved={forms.mark('settings')} />}
            {P.names
              ? <People id={id} event={ev} list={l} can={P} onChanged={changed} mark={forms.mark} />
              : <p className="note">Du ser antall. Navnene på listen ser de som har tilgangen «Påmeldingslister» eller «Innsjekking».</p>}
          </>
        )}
        {/* Arrangementer pro (2026-10-31): the tickets sold for the event, checked in at the door (behind event_tickets). */}
        {ticketsOn && <TicketList eventId={id} />}
        <Msg text={msg.text} tone={msg.tone} />
      </div>
    </Card>
  );
}

/** «Svar i appen»: the members' own «Kommer» / «Kommer ikke» — counted here, named under Arrangementer. */
function AppAnswers({ a }: { a: Body | null | undefined }) {
  if (!a || (!a.coming && !a.notComing)) return null;
  return (
    <>
      <ul className="reg-tiles" aria-label="Svar i appen">
        <li><b>{a.coming}</b><span>kommer (svar i appen){a.comingPeople > a.coming ? ` · ${people(a.comingPeople)} med følge` : ''}</span></li>
        <li><b>{a.notComing}</b><span>kommer ikke (svar i appen)</span></li>
      </ul>
      <p className="note">Svar fra appen står ikke på påmeldingslisten under.{a.checkedIn ? ` ${people(a.checkedIn)} med svar i appen er sjekket inn.` : ''}</p>
    </>
  );
}

function Tiles({ s, app }: { s: Body; app?: Body | null }) {
  // «kommet» counts the app's «Kommer» answers checked in at the door too (084); the list's own figure stays beside it.
  const appIn = (app && app.checkedInSeats) || 0;
  return (
    <ul className="reg-tiles" aria-label="Oppsummering">
      <li><b>{s.registeredSeats}</b><span>{s.registeredSeats === 1 ? 'plass påmeldt' : 'plasser påmeldt'} · {people(s.registeredEntries)}</span></li>
      {s.capacity !== null && <li><b>{s.remaining}</b><span>ledige av {s.capacity}</span></li>}
      <li><b>{s.waitlistedSeats}</b><span>på venteliste{s.waitlistedEntries ? ` · ${people(s.waitlistedEntries)}` : ''}</span></li>
      <li><b>{s.checkedInSeats + appIn}</b><span>kommet{s.checkedInEntries ? ` · ${people(s.checkedInEntries)} fra listen` : ''}{appIn ? ` · ${seats(appIn)} fra appen` : ''}</span></li>
      {s.cancelledEntries > 0 && <li><b>{s.cancelledEntries}</b><span>avmeldt</span></li>}
      {s.notArrivedEntries !== null && <li><b>{s.notArrivedEntries}</b><span>kom ikke</span></li>}
    </ul>
  );
}

/* ---- «Svar i appen» by name, with check-in (084, owner 2026-11-01) ----
 * The members' own «Kommer» answers, named as Arrangementer names them, for those who may see the list's names; «Sjekk inn» /
 * «Angre» for events.checkin, like the list — so the register can say who came. Every look is in the activity log. */
function AppAnswerList({ id, can: P, onChanged }: { id: string; can: Body; onChanged: (b: Body) => void }) {
  const base = `/event-ops/events/${encodeURIComponent(id)}/answers`;
  const list = useLoad<Body>(base);
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [said, setSaid] = useState('');
  const [, say] = useMsg();
  const checkIn = async (a: Body, checkedIn: boolean) => {
    const who = a.name || 'Personen';
    setPending((p) => ({ ...p, [a.userId]: checkedIn }));
    const r = await send(`${base}/${encodeURIComponent(a.userId)}/checkin`, { method: 'PUT', body: JSON.stringify({ checkedIn }) });
    setPending((p) => { const n = { ...p }; delete n[a.userId]; return n; });
    if (!r.ok) { setSaid(`${who}: ikke lagret.`); return say(errText(r.body), 'err'); }
    setSaid(`${who}: ${checkedIn ? 'sjekket inn' : 'innsjekkingen er angret'}.`);
    onChanged(r.body);
    // The answer comes back with the result: patched in place, so a tap is not a second look at the names (activity log).
    if (r.body.answer && list.data) list.setData({ ...list.data, appAnswers: { ...list.data.appAnswers,
      items: (list.data.appAnswers.items || []).map((x: Body) => (x.userId === r.body.answer.userId ? r.body.answer : x)) } });
  };
  const items: Body[] = (list.data && list.data.appAnswers && list.data.appAnswers.items) || [];
  return (
    <section className="work-comments" aria-labelledby="reg-app-head">
      <h4 id="reg-app-head">Svar i appen</h4>
      <p className="vh" role="status" aria-live="polite">{said}</p>
      {list.error ? <LoadError error={list.error} retry={list.reload} /> : !list.data ? <Loading what="Henter svarene …" /> : (
        <ul className="reg-people">{items.map((a) => {
          const shown = a.userId in pending ? pending[a.userId] : Boolean(a.checkedIn);
          return (
            <li key={a.userId} className={'reg-person' + (a.userId in pending ? ' pending' : '')}>
              <span className="reg-name">
                <b><bdi dir="auto">{a.name || 'Uten navn'}</bdi>{a.partySize > 1 && <span className="note"> · {a.partySize} personer</span>}</b>
                <span className="work-row-meta">
                  <Tag tone="grey">Kommer (appen)</Tag>
                  {a.memberNo && <span className="note">Medlem {a.memberNo}</span>}
                  {shown && <Tag tone="green">Kommet{a.checkedInBy && a.checkedInBy.name ? ` · ${a.checkedInBy.name}` : ''}</Tag>}
                  {a.userId in pending && <span>Lagrer …</span>}
                </span>
                {a.note && <span className="note">{a.note}</span>}
              </span>
              {P.checkin && a.canCheckIn && (
                <Btn className={'btn sm reg-checkin' + (shown ? ' ghost' : '')} disabled={a.userId in pending} aria-busy={a.userId in pending || undefined} aria-pressed={shown}
                     aria-label={`${shown ? 'Angre innsjekking for' : 'Sjekk inn'} ${a.name || 'personen'}`} onClick={() => checkIn(a, !shown)}>
                  {shown ? 'Angre' : 'Sjekk inn'}
                </Btn>
              )}
            </li>
          );
        })}</ul>
      )}
      <p className="note" style={{ marginTop: 10 }}>Hvert oppslag i navnene føres i aktivitetsloggen (hvem så, hvilket arrangement, hvor mange).</p>
    </section>
  );
}

/* ---- the list's settings (events.registrations.manage) ---- */
function ListSettings({ id, list, canCapacity, onChanged, onUnsaved }: {
  id: string; list: Body; canCapacity: boolean; onChanged: (b: Body) => void; onUnsaved: (d: boolean) => void;
}) {
  const [f, setF] = useState({ capacity: list.capacity === null ? '' : String(list.capacity), waitlist: Boolean(list.waitlist) });
  const [msg, say] = useMsg();
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState<Body | null>(null);
  const dirty = f.capacity !== (list.capacity === null ? '' : String(list.capacity)) || f.waitlist !== Boolean(list.waitlist);
  useEffect(() => { onUnsaved(dirty); }, [dirty, onUnsaved]);
  useEffect(() => () => onUnsaved(false), [onUnsaved]);   // a form that goes away is not unsaved
  const put = async (body: Body, done: string, version = list.version) => {
    setBusy(true); say('Lagrer …');
    const r = await send(`/event-ops/events/${encodeURIComponent(id)}/list`, { method: 'PUT', body: JSON.stringify({ ...body, version }) });
    setBusy(false);
    if (r.status === 409 && r.body.list) { setConflict(r.body); return say('', ''); }
    if (!r.ok) return say(errText(r.body, FIELD_LABELS), 'err');
    setConflict(null); onChanged(r.body); say(done, 'ok');
  };
  const capacityBody = () => ({ capacity: f.capacity === '' ? null : Number(f.capacity), waitlist: f.capacity === '' ? false : f.waitlist });
  const saveCapacity = (version?: number) => put(capacityBody(), 'Kapasiteten er lagret.', version);
  return (
    <div className="reg-settings" role="group" aria-label="Listens innstillinger">
      <div className="row">
        {list.state === 'open'
          ? <Btn ghost disabled={busy} onClick={() => put({ state: 'closed' }, 'Listen er stengt for nye påmeldinger. De som står på den kan fortsatt endres og sjekkes inn.')}>Steng for nye</Btn>
          : list.state === 'closed' ? <Btn ghost disabled={busy} onClick={() => put({ state: 'open' }, 'Listen er åpen igjen.')}>Åpne igjen</Btn> : null}
        {list.state !== 'cancelled' && <TwoStep label="Avlys listen" confirmLabel="Ja, avlys listen (kan ikke åpnes igjen)" disabled={busy}
          onConfirm={() => put({ state: 'cancelled' }, 'Listen er avlyst. Den kan bare leses nå.')} />}
      </div>
      {canCapacity ? (
        <div className="row">
          <Field label="Kapasitet" note="Antall plasser. Tomt betyr ingen grense.">{(fid, nid) => (
            <input id={fid} type="number" inputMode="numeric" min={1} max={10000} value={f.capacity} aria-describedby={nid} disabled={busy}
                   onChange={(e) => setF({ ...f, capacity: e.target.value.replace(/[^\d]/g, '') })} />
          )}</Field>
          <label className="check work-check"><input type="checkbox" checked={f.waitlist} disabled={busy || f.capacity === ''} onChange={(e) => setF({ ...f, waitlist: e.target.checked })} /> Venteliste når det er fullt</label>
          <Btn disabled={!dirty || busy} aria-busy={busy || undefined} onClick={() => saveCapacity()}>{busy ? 'Lagrer …' : 'Lagre kapasitet'}</Btn>
        </div>
      ) : <p className="note">Kapasitet og venteliste er ikke slått på for moskeen. Listen har ingen grense.</p>}
      {conflict && <Conflict what="listen" busy={busy} onKeepMine={() => saveCapacity(conflict.list.version)}
        onTakeTheirs={() => { onChanged(conflict); setConflict(null); setF({ capacity: conflict.list.capacity === null ? '' : String(conflict.list.capacity), waitlist: Boolean(conflict.list.waitlist) }); say('Endringen din er forkastet. Dette er den nyeste versjonen.', 'ok'); }} />}
      <Msg text={msg.text} tone={msg.tone} />
    </div>
  );
}

/* ---- the people on the list (names: manage · checkin · attendees.read) ---- */
function People({ id, event, list, can: P, onChanged, mark }: {
  id: string; event: Body; list: Body; can: Body; onChanged: (b: Body) => void; mark: (form: string) => (d: boolean) => void;
}) {
  const [view, setView] = useState('active');
  const [arrived, setArrived] = useState('any');
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => { const t = setTimeout(() => setQ(typed.trim()), 250); return () => clearTimeout(t); }, [typed]);
  const qs = new URLSearchParams({ status: view, arrived, ...(q ? { q } : {}) });
  const base = `/event-ops/events/${encodeURIComponent(id)}/registrations`;
  const page = usePaged(`${base}?${qs.toString()}`, (d) => d.registrations);
  const [said, setSaid] = useState('');
  // A check-in shows at once, and goes back if the server refuses it.
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [msg, say] = useMsg();
  const refresh = (body?: Body) => { void page.reload(); if (body) onChanged(body); };
  const checkIn = async (e: Body, checkedIn: boolean) => {
    setPending((p) => ({ ...p, [e.id]: checkedIn }));
    setSaid(`${e.displayName}: lagrer …`);
    const r = await send(`${base}/${encodeURIComponent(e.id)}/checkin`, { method: 'PUT', body: JSON.stringify({ checkedIn }) });
    setPending((p) => { const n = { ...p }; delete n[e.id]; return n; });
    if (!r.ok) { setSaid(`${e.displayName}: ikke lagret.`); return say(errText(r.body), 'err'); }
    setSaid(`${e.displayName}: ${checkedIn ? 'sjekket inn' : 'innsjekkingen er angret'}. ${r.body.summary.checkedInSeats} kommet.`);
    refresh(r.body);
  };
  const promote = async (e: Body) => {
    say('Flytter opp …');
    const r = await post(`${base}/${encodeURIComponent(e.id)}/promote`, { version: e.version });
    if (r.status === 409 && r.body.registration) { refresh(); return say(r.body.message + ' Listen er oppdatert.', 'warn'); }
    if (!r.ok) return say(errText(r.body), 'err');
    refresh(r.body); say(`${e.displayName} er flyttet fra ventelisten til påmeldt.`, 'ok');
  };
  const filtersOn = [view !== 'active', arrived !== 'any', q].filter(Boolean).length;
  const writable = Boolean(P.writable);
  return (
    <section className="work-comments" aria-labelledby="reg-people-head">
      <h4 id="reg-people-head">Hvem kommer</h4>
      <div className="work-bar" style={{ margin: '6px 0 0' }}>
        <div className="bnr-seg work-seg work-due-seg" role="group" aria-label="Status">
          {PEOPLE_VIEWS.map(([v, l]) => <button key={v} type="button" aria-pressed={view === v} onClick={() => setView(v)}>{l}</button>)}
        </div>
      </div>
      <div className="work-bar" style={{ margin: '6px 0 0' }}>
        <div className="bnr-seg work-seg" role="group" aria-label="Oppmøte">
          {ARRIVED.map(([v, l]) => <button key={v} type="button" aria-pressed={arrived === v} onClick={() => setArrived(v)}>{l}</button>)}
        </div>
        <Field label="Søk etter navn" className="field work-search">{(fid) => (
          <input id={fid} type="search" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Navn" maxLength={120} autoComplete="off" />
        )}</Field>
      </div>
      <p className="vh" role="status" aria-live="polite">{said}</p>
      {page.error ? <LoadError error={page.error} retry={page.reload} /> : !page.data ? <Loading what="Henter listen …" /> : (
        <>
          <p className="note" role="status">{page.total === 1 ? '1 person' : `${page.total} personer`}{page.items.length < page.total ? `, ${page.items.length} vist` : ''}</p>
          {page.items.length === 0 && <p className="work-empty">{filtersOn ? 'Ingen passer filteret.' : 'Ingen på listen ennå.'}</p>}
          <ul className="reg-people">{page.items.map((e) => {
            const shown = e.id in pending ? pending[e.id] : Boolean(e.checkedIn);
            const [sl, st] = STATUS[e.status] || [e.status, 'grey'];
            return (
              <li key={e.id} className={'reg-person' + (e.status === 'cancelled' ? ' cancelled' : '') + (e.id in pending ? ' pending' : '')}>
                <span className="reg-name">
                  <b><bdi dir="auto">{e.displayName}</bdi>{e.partySize > 1 && <span className="note"> · {e.partySize} personer</span>}</b>
                  <span className="work-row-meta">
                    <Tag tone={st}>{sl}{e.queuePosition ? ` nr. ${e.queuePosition}` : ''}</Tag>
                    {shown && <Tag tone="green">Kommet{e.checkedInBy ? ` · ${e.checkedInBy.name}` : ''}</Tag>}
                    {e.id in pending && <span>Lagrer …</span>}
                  </span>
                  {e.note && <span className="note">{e.note}</span>}
                </span>
                {P.checkin && e.status === 'registered' && writable && (
                  <Btn className={'btn sm reg-checkin' + (shown ? ' ghost' : '')} disabled={e.id in pending} aria-busy={e.id in pending || undefined} aria-pressed={shown}
                       aria-label={`${shown ? 'Angre innsjekking for' : 'Sjekk inn'} ${e.displayName}`} onClick={() => checkIn(e, !shown)}>
                    {shown ? 'Angre' : 'Sjekk inn'}
                  </Btn>
                )}
                {P.manage && P.capacity && e.status === 'waitlisted' && writable && (
                  <Btn ghost onClick={() => promote(e)} aria-label={`Flytt ${e.displayName} fra ventelisten til påmeldt`}>Flytt opp</Btn>
                )}
                {P.manage && e.status !== 'cancelled' && writable && <EditEntry base={base} entry={e} onChanged={refresh} onUnsaved={mark('edit:' + e.id)} />}
              </li>
            );
          })}</ul>
          <MoreButton list={page} />
        </>
      )}
      {P.manage && writable && list.state === 'open' && <AddEntry base={base} canCheckIn={Boolean(P.checkin)} onAdded={(b) => { refresh(b); }} onUnsaved={mark('add')} />}
      {P.manage && writable && list.state === 'closed' && <p className="note">Listen er stengt for nye påmeldinger. Åpne den igjen for å legge til.</p>}
      <Msg text={msg.text} tone={msg.tone} />
      <p className="note" style={{ marginTop: 10 }}>Hvert oppslag i navnene føres i aktivitetsloggen (hvem så, hvilken liste, hvor mange). {event.status === 'published' ? '' : ''}</p>
    </section>
  );
}

/* ---- add a person (manage) ----
 * Owner 2026-10-23: in a popup from «+ Legg til person». At the door people come one after another, so after a person
 * is added the popup stays open with the result and the name field empty and focused, ready for the next; «×» or
 * Escape closes it and the last result stays on the page. */
function AddEntry({ base, canCheckIn, onAdded, onUnsaved }: { base: string; canCheckIn: boolean; onAdded: (b: Body) => void; onUnsaved: (d: boolean) => void }) {
  const empty = { displayName: '', partySize: '1', note: '', checkIn: false };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState<Body | null>(null);
  const [msg, say] = useMsg();
  const dirty = Boolean(f.displayName || f.note);
  useEffect(() => { onUnsaved(dirty); }, [dirty, onUnsaved]);
  useEffect(() => () => onUnsaved(false), [onUnsaved]);   // a form that goes away is not unsaved
  const nameRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const close = () => { setOpen(false); setDup(null); setF(empty); };
  const add = async (allowDuplicate = false) => {
    setBusy(true); say('Legger til …'); setDup(null);
    const r = await post(base, { displayName: f.displayName, partySize: Number(f.partySize) || 1, note: f.note || null,
      ...(canCheckIn && f.checkIn ? { checkIn: true } : {}), ...(allowDuplicate ? { allowDuplicate: true } : {}) });
    setBusy(false);
    if (r.status === 409 && r.body.code === 'duplicate') { setDup(r.body); return say('', ''); }
    if (!r.ok) return say(errText(r.body, FIELD_LABELS), 'err');
    setF(empty);
    const reg: Body = r.body.registration;
    say(`${reg.displayName} er ${reg.status === 'waitlisted' ? `satt på ventelisten (nr. ${reg.queuePosition})` : 'påmeldt'}${reg.checkedIn ? ' og sjekket inn' : ''}.`, 'ok');
    onAdded(r.body);
    nameRef.current?.focus();
  };
  return (
    <>
    <div className="new-row"><Btn small={false} onClick={() => { say(''); setOpen(true); }}>+ Legg til person</Btn></div>
    {!open && <Msg text={msg.text} tone={msg.tone} />}
    <FormDialog open={open} title="Legg til person" dirty={dirty} onClose={close}>
    <div className="reg-add">
      <div className="row">
        <Field label="Navn" required className="field work-search">{(fid) => (
          <input id={fid} ref={nameRef} value={f.displayName} maxLength={120} disabled={busy} autoComplete="off" onChange={(e) => setF({ ...f, displayName: e.target.value })} />
        )}</Field>
        <Field label="Antall" note="Personer i følget, 1–20." style={{ minWidth: 110, maxWidth: 140 }}>{(fid, nid) => (
          <input id={fid} type="number" inputMode="numeric" min={1} max={20} value={f.partySize} aria-describedby={nid} disabled={busy}
                 onChange={(e) => setF({ ...f, partySize: e.target.value.replace(/[^\d]/g, '') })} />
        )}</Field>
      </div>
      <Field label="Notat" note="Bare for de ansatte. Skriv ikke helseopplysninger eller annet sensitivt.">{(fid, nid) => (
        <input id={fid} value={f.note} maxLength={500} aria-describedby={nid} disabled={busy} onChange={(e) => setF({ ...f, note: e.target.value })} />
      )}</Field>
      {canCheckIn && <label className="check work-check"><input type="checkbox" checked={f.checkIn} disabled={busy} onChange={(e) => setF({ ...f, checkIn: e.target.checked })} /> Er her nå — sjekk inn samtidig</label>}
      {dup && (
        <div className="msg warn work-conflict" role="alert">
          <p><b>{dup.message}</b></p>
          <div className="row">
            <Btn disabled={busy} onClick={() => add(true)}>Legg til likevel (en annen person)</Btn>
            <Btn ghost disabled={busy} onClick={() => { setDup(null); setF(empty); say('Ikke lagt til.', 'ok'); }}>Ikke legg til</Btn>
          </div>
        </div>
      )}
      {!dup && <div className="row"><Btn disabled={!f.displayName.trim() || busy} aria-busy={busy || undefined} onClick={() => add(false)}>{busy ? 'Legger til …' : 'Legg til'}</Btn></div>}
      <Msg text={msg.text} tone={msg.tone} />
    </div>
    </FormDialog>
    </>
  );
}

/* ---- change or cancel one entry (manage) ---- */
function EditEntry({ base, entry, onChanged, onUnsaved }: { base: string; entry: Body; onChanged: (b?: Body) => void; onUnsaved: (d: boolean) => void }) {
  const start = { displayName: entry.displayName, partySize: String(entry.partySize), note: entry.note || '' };
  const [f, setF] = useState(start);
  const [openEdit, setOpenEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState<Body | null>(null);
  const [msg, say] = useMsg();
  const dirty = f.displayName !== start.displayName || f.partySize !== String(entry.partySize) || f.note !== (entry.note || '');
  useEffect(() => { onUnsaved(openEdit && dirty); }, [openEdit, dirty, onUnsaved]);
  useEffect(() => () => onUnsaved(false), [onUnsaved]);
  const save = async (body: Body, done: string, version = entry.version) => {
    setBusy(true); say('Lagrer …');
    const r = await patch(`${base}/${encodeURIComponent(entry.id)}`, { ...body, version });
    setBusy(false);
    if (r.status === 409 && r.body.registration) { setConflict(r.body.registration); return say('', ''); }
    if (!r.ok) return say(errText(r.body, FIELD_LABELS), 'err');
    setConflict(null); setOpenEdit(false); onChanged(r.body); say(done, 'ok');
  };
  const edits = () => ({ displayName: f.displayName, partySize: Number(f.partySize) || entry.partySize, note: f.note || null });
  return (
    <details className="reg-edit" open={openEdit} onToggle={(e) => setOpenEdit((e.target as HTMLDetailsElement).open)} style={{ flex: '1 1 100%' }}>
      <summary>Endre eller meld av {entry.displayName}</summary>
      <div className="row">
        <Field label="Navn" required className="field work-search">{(fid) => <input id={fid} value={f.displayName} maxLength={120} disabled={busy} onChange={(e) => setF({ ...f, displayName: e.target.value })} />}</Field>
        <Field label="Antall" style={{ minWidth: 110, maxWidth: 140 }}>{(fid) => (
          <input id={fid} type="number" inputMode="numeric" min={1} max={20} value={f.partySize} disabled={busy} onChange={(e) => setF({ ...f, partySize: e.target.value.replace(/[^\d]/g, '') })} />
        )}</Field>
      </div>
      <Field label="Notat">{(fid) => <input id={fid} value={f.note} maxLength={500} disabled={busy} onChange={(e) => setF({ ...f, note: e.target.value })} />}</Field>
      {conflict && <Conflict what="påmeldingen" busy={busy} onKeepMine={() => save(edits(), 'Endringen er lagret.', conflict.version)}
        onTakeTheirs={() => { setConflict(null); setOpenEdit(false); onChanged(); say('Endringen din er forkastet. Listen viser den nyeste versjonen.', 'ok'); }} />}
      <div className="row">
        <Btn disabled={!dirty || busy || conflict !== null} aria-busy={busy || undefined} onClick={() => save(edits(), 'Endringen er lagret.')}>{busy ? 'Lagrer …' : 'Lagre'}</Btn>
        {entry.checkedIn
          ? <span className="note">Personen er sjekket inn. Angre innsjekkingen før en avmelding.</span>
          : <TwoStep label="Meld av" confirmLabel={`Ja, meld av ${entry.displayName}`} disabled={busy} onConfirm={() => save({ status: 'cancelled' }, `${entry.displayName} er meldt av.`)} />}
      </div>
      <Msg text={msg.text} tone={msg.tone} />
    </details>
  );
}
